import { useMemo, useRef, useState } from 'react';
import type { Project } from '../projects';
import type { BudgetRollup } from '../projects';
import { costCurve, type CostStep } from '../budget-graph';
import { fmtMoney, currencySymbol } from '../money';
import { MONTHS } from '../when';

/*
 * THE BUDGET BURN-UP — cost over time, against the budget and the forecast.
 *
 * The second chart in the app, and the same rules as the first (Burndown). The
 * arithmetic is in budget-graph.ts and tested there; this only draws it. Paid
 * climbs as a filled line, committed stacks on top to the running total, and
 * both are read against the approved-budget ceiling and the forecast — the shape
 * a PM expects for cost. Drawn by hand in SVG, in the app's CSS-variable colours,
 * with no charting library — for the reasons Burndown.tsx sets out.
 *
 * THE CHART IS THE EVIDENCE; THE SENTENCE IS THE ANSWER. The line above says in
 * words whether the spend is inside the budget and whether the forecast clears
 * it, because that is the reading, and it is also the only part a screen reader
 * gets.
 */

const W = 560;
const H = 210;
// `left` holds the y-axis figures, which are right-anchored just inside it. It
// has to clear the WIDEST of them — the top tick, a currency figure like
// "TT$1910k" (a three-character symbol + up to five digits + "k"). At 48 that
// label ran past the left edge and the SVG's overflow:hidden clipped its first
// character ("T$1910k"). 64 leaves the widest realistic figure a few units of
// air; the plot keeps ample width (W − left − right).
const PAD = { top: 16, right: 104, bottom: 26, left: 64 };

/** A compact axis figure in the workspace currency — "$637k", "$40k". */
function short(n: number): string {
  const s = currencySymbol();
  if (n >= 1000) return `${s}${Math.round(n / 1000)}k`;
  return `${s}${Math.round(n)}`;
}

/** A day-number back to "12 Aug". */
function dayLabel(day: number): string {
  const d = new Date(day * 86400000);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
}

export default function BudgetGraph({
  project,
  rollup,
  base,
  rates,
}: {
  project: Project;
  rollup: BudgetRollup;
  base?: string;
  rates?: Record<string, number> | null;
}) {
  const curve = useMemo(() => costCurve(project, { base, rates }), [project, base, rates]);
  // Where the pointer is along the x-axis (in the chart's own coordinate space),
  // so the reading follows the cursor across the whole width, not just the dated
  // points. Cleared on leave. The svg ref maps a screen x back to chart space.
  const [hoverX, setHoverX] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);

  // Nothing dated to plot. If there are lines but none carry a date, say so
  // plainly (like the burndown's unsized caveat); if there are none at all, the
  // ledger's own empty state already covers it, so draw nothing.
  if (curve.steps.length < 1) {
    if (curve.undated.count > 0) {
      return (
        <div className="bud-graph">
          <p className="bg-say">
            {curve.undated.count} {curve.undated.count === 1 ? 'line has' : 'lines have'} no date, so there is
            nothing to chart over time yet. Add a date to a line to see the spend climb.
          </p>
        </div>
      );
    }
    return null;
  }

  const last = curve.steps[curve.steps.length - 1];
  const from = curve.from!;
  const to = curve.to!;
  const span = Math.max(1, to - from);
  const top = Math.max(curve.budget ?? 0, curve.forecast ?? 0, last.paid + last.committed, 1) * 1.06;

  const x = (day: number) => PAD.left + ((day - from) / span) * (W - PAD.left - PAD.right);
  const y = (v: number) => PAD.top + (1 - v / top) * (H - PAD.top - PAD.bottom);
  const baseY = y(0);

  const paidPts = (s: CostStep) => `${x(s.day)},${y(s.paid)}`;
  const totalPts = (s: CostStep) => `${x(s.day)},${y(s.paid + s.committed)}`;
  const paidLine = curve.steps.map(paidPts).join(' ');
  const totalLine = curve.steps.map(totalPts).join(' ');
  // The paid area sits under the paid line; the committed band fills between the
  // paid line and the total line, so its thickness IS the committed money.
  const paidArea = `${x(from)},${baseY} ${paidLine} ${x(last.day)},${baseY}`;
  const committedBand = `${paidLine} ${[...curve.steps].reverse().map(totalPts).join(' ')}`;

  // The reading follows the cursor across the plot. The x is clamped to the axis;
  // the date under it is read off, and the figures are the cumulative spend AS OF
  // that date — the last dated step at or before it (nothing between events, so it
  // is flat there, which is the honest reading). The guide + card sit at the
  // cursor, so sliding moves them smoothly even when only a couple of lines carry
  // dates.
  const plotLeft = PAD.left;
  const plotRight = W - PAD.right;
  const plotW = Math.max(1, plotRight - plotLeft);
  const hv = (() => {
    if (hoverX == null) return null;
    const hx = Math.max(plotLeft, Math.min(hoverX, plotRight));
    const day = Math.round(from + ((hx - plotLeft) / plotW) * span);
    let paid = 0;
    let committed = 0;
    for (const s of curve.steps) {
      if (s.day <= day) { paid = s.paid; committed = s.committed; } else break;
    }
    const total = paid + committed;
    let vs = '';
    if (rollup.budget != null) {
      const rem = rollup.budget - total;
      vs = rem >= 0
        ? `${fmtMoney(rem)} of the ${fmtMoney(rollup.budget)} budget left`
        : `${fmtMoney(-rem)} over the ${fmtMoney(rollup.budget)} budget`;
    }
    // Keep the card inside the plot: percentage across, clamped so its centred
    // width does not spill off either edge.
    const leftPct = Math.max(15, Math.min((hx / W) * 100, 85));
    return { x: hx, date: dayLabel(day), paid, committed, total, vs, leftPct };
  })();

  const onHoverMove = (e: React.MouseEvent) => {
    const svg = svgRef.current;
    if (!svg) return;
    const r = svg.getBoundingClientRect();
    setHoverX(((e.clientX - r.left) / r.width) * W);
  };

  const spent = rollup.actualTotal + rollup.committedTotal;
  const overBudget = rollup.budget != null && spent > rollup.budget;
  const fcOver = project.forecast != null && rollup.budget != null && project.forecast > rollup.budget;

  // Budget and forecast are often within a few percent of each other, which puts
  // their two reference labels on top of one another. Nudge them apart around
  // their midpoint when that happens, keeping each near its own line and clear of
  // the top edge.
  const labelBaseline = 3;
  let budgetLabelY = curve.budget != null ? y(curve.budget) : 0;
  let forecastLabelY = curve.forecast != null ? y(curve.forecast) : 0;
  if (curve.budget != null && curve.forecast != null && Math.abs(budgetLabelY - forecastLabelY) < 14) {
    const mid = (budgetLabelY + forecastLabelY) / 2;
    budgetLabelY = curve.budget >= curve.forecast ? mid - 7 : mid + 7;
    forecastLabelY = curve.budget >= curve.forecast ? mid + 7 : mid - 7;
  }
  budgetLabelY = Math.max(budgetLabelY, PAD.top + 8);
  forecastLabelY = Math.max(forecastLabelY, PAD.top + 8);

  return (
    <div className="bud-graph">
      <p className="bg-say">
        {rollup.budget != null ? (
          <>
            <b>Committed + paid {fmtMoney(spent)}</b> of the <b>{fmtMoney(rollup.budget)}</b> budget
            {overBudget ? ' — already over it.' : `, ${fmtMoney(rollup.budget - spent)} still to run.`}
            {project.forecast != null &&
              (Math.round(spent) > Math.round(project.forecast)
                ? ` The ${fmtMoney(project.forecast)} forecast now sits ${fmtMoney(spent - project.forecast)} under what is already committed.`
                : fcOver
                ? ` The ${fmtMoney(project.forecast)} forecast sits ${fmtMoney(project.forecast - rollup.budget)} over.`
                : ` The ${fmtMoney(project.forecast)} forecast lands inside it.`)}
          </>
        ) : (
          <>
            <b>Committed + paid {fmtMoney(spent)}</b> so far. Set a budget to read it against a baseline.
          </>
        )}
      </p>
      <div className="bg-plot">
      <svg
        ref={svgRef}
        className="bg-svg"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={described(rollup, project)}
        onMouseMove={onHoverMove}
        onMouseLeave={() => setHoverX(null)}
      >
        {/* axes */}
        <line className="bg-axis" x1={PAD.left} y1={PAD.top} x2={PAD.left} y2={baseY} />
        <line className="bg-axis" x1={PAD.left} y1={baseY} x2={W - PAD.right} y2={baseY} />
        {/* y ticks */}
        <text className="bg-tick" x={PAD.left - 5} y={baseY + 3} textAnchor="end">0</text>
        <text className="bg-tick" x={PAD.left - 5} y={y(top / 1.06) + 3} textAnchor="end">{short(top / 1.06)}</text>

        {/* forecast, then budget — references, dashed */}
        {curve.forecast != null && (
          <>
            <line className={`bg-forecast${fcOver ? ' over' : ''}`} x1={PAD.left} y1={y(curve.forecast)} x2={W - PAD.right} y2={y(curve.forecast)} />
            <text className={`bg-reflabel${fcOver ? ' over' : ''}`} x={W - PAD.right + 5} y={forecastLabelY + labelBaseline}>
              Forecast {short(curve.forecast)}
            </text>
          </>
        )}
        {curve.budget != null && (
          <>
            <line className="bg-budget" x1={PAD.left} y1={y(curve.budget)} x2={W - PAD.right} y2={y(curve.budget)} />
            <text className="bg-reflabel" x={W - PAD.right + 5} y={budgetLabelY + labelBaseline}>
              Budget {short(curve.budget)}
            </text>
          </>
        )}

        {/* committed band (behind), paid area, then the two lines */}
        {last.committed > 0 && <polygon className="bg-committed-fill" points={committedBand} />}
        <polygon className="bg-paid-fill" points={paidArea} />
        <polyline className="bg-paid" points={paidLine} fill="none" />
        <polyline className="bg-total" points={totalLine} fill="none" />
        <circle className="bg-now" cx={x(last.day)} cy={y(last.paid + last.committed)} r={3.5} />

        {/* x ticks: first, last, and the go-live when it is on the axis */}
        <text className="bg-tick" x={x(from)} y={H - 8}>{dayLabel(curve.steps[0].day)}</text>
        <text className="bg-tick" x={x(last.day)} y={H - 8} textAnchor="middle">{dayLabel(last.day)}</text>
        {curve.goLiveDay != null && curve.goLiveDay > last.day && (
          <text className="bg-tick" x={W - PAD.right} y={H - 8} textAnchor="end">{dayLabel(curve.goLiveDay)} Go-Live</text>
        )}

        {/* One transparent catcher over the whole plot so the pointer is tracked
            across the full width, and a guide that follows the cursor. */}
        <rect className="bg-hit" x={plotLeft} y={PAD.top} width={plotW} height={baseY - PAD.top} />
        {hv && <line className="bg-guide" x1={hv.x} y1={PAD.top} x2={hv.x} y2={baseY} />}
      </svg>
      {/* The reading at the cursor — a plain HTML card so it unmounts cleanly
          (an SVG foreignObject leaves paint artefacts when it toggles off). */}
      {hv && (
        <div className="bg-tip" style={{ left: `${hv.leftPct}%` }}>
          <div className="bg-tip-d">As of {hv.date}</div>
          <div className="bg-tip-r"><span><i className="bg-sw paid" />Paid to date</span><b>{fmtMoney(hv.paid)}</b></div>
          <div className="bg-tip-r"><span><i className="bg-sw total" />Committed</span><b>{fmtMoney(hv.committed)}</b></div>
          <div className="bg-tip-r tot"><span>Total</span><b>{fmtMoney(hv.total)}</b></div>
          {hv.vs && <div className="bg-tip-vs">{hv.vs}</div>}
        </div>
      )}
      </div>
      <div className="bg-legend">
        <span><i className="bg-sw paid" />Paid to date</span>
        <span><i className="bg-sw total" />Committed + paid</span>
        {curve.budget != null && <span><i className="bg-sw bud" />Budget</span>}
        {curve.forecast != null && <span><i className="bg-sw fc" />Forecast</span>}
      </div>
      {curve.undated.count > 0 && (
        <p className="bg-caveat">
          {curve.undated.count} {curve.undated.count === 1 ? 'line has' : 'lines have'} no date, so{' '}
          {curve.undated.count === 1 ? 'it is' : 'they are'} in the totals but not on the line.
        </p>
      )}
    </div>
  );
}

/** The chart as a sentence, for anybody who cannot see it. */
function described(rollup: BudgetRollup, project: Project): string {
  const spent = rollup.actualTotal + rollup.committedTotal;
  const b = rollup.budget != null ? ` against a ${fmtMoney(rollup.budget)} budget` : '';
  const f = project.forecast != null ? `, forecast ${fmtMoney(project.forecast)}` : '';
  return `Cost over time. Paid ${fmtMoney(rollup.actualTotal)}, committed ${fmtMoney(rollup.committedTotal)} — ${fmtMoney(spent)} in all${b}${f}.`;
}
